import { Check, ChevronDown, ChevronRight, ChevronsDown, ChevronsUp, Copy } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn, showAppToast } from '@/lib/utils';

export function StructuredDataView({
  title,
  content,
  className,
}: {
  title?: string;
  content: string;
  className?: string;
}): JSX.Element {
  const [showRaw, setShowRaw] = useState(false);
  const parsed = useMemo(() => {
    try {
      return JSON.parse(content) as unknown;
    } catch {
      return null;
    }
  }, [content]);

  if (parsed === null) {
    return (
      <pre className={`max-h-56 overflow-auto rounded-md border bg-muted/20 p-3 text-xs leading-5 text-foreground whitespace-pre-wrap ${className ?? ''}`}>
        {content}
      </pre>
    );
  }

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between gap-2">
        {title ? <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div> : <div />}
        <div className="flex items-center gap-2">
          {showRaw ? (
            <CopyButton
              label="Copy raw JSON"
              value={content}
              toastTitle="Copied group"
              toastMessage={title ?? 'Raw JSON'}
            />
          ) : null}
          <Button type="button" variant="outline" size="sm" onClick={() => setShowRaw((value) => !value)}>
            {showRaw ? 'Readable' : 'Raw'}
          </Button>
        </div>
      </div>
      {showRaw ? (
        <pre className="max-h-56 overflow-auto rounded-md border bg-muted/20 p-3 text-xs leading-5 text-foreground whitespace-pre-wrap">
          {content}
        </pre>
      ) : (
        <div className="max-h-72 overflow-auto rounded-md border bg-background p-3">
          <StructuredNode value={parsed} depth={0} />
        </div>
      )}
    </div>
  );
}

function StructuredNode({ value, depth, label }: { value: unknown; depth: number; label?: string }): JSX.Element {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <FieldRow label={label ?? 'Value'} value="None" depth={depth} muted />;
    }
    return label ? <ArrayGroup value={value} depth={depth} label={label} /> : <ArrayGroup value={value} depth={depth} />;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return <FieldRow label={label ?? 'Value'} value="None" depth={depth} muted />;
    }
    const { userFacingEntries, internalEntries } = partitionObjectEntries(entries);
    return (
      <div className={depth > 0 ? 'space-y-2' : 'space-y-3'}>
        {label ? <SectionLabel label={label} depth={depth} /> : null}
        {userFacingEntries.map(([key, child]) => {
          if (Array.isArray(child)) {
            if (child.length === 0) {
              return (
                <FieldRow
                  key={key}
                  label={humanizeKey(key)}
                  value="None"
                  depth={depth + 1}
                  muted
                />
              );
            }
            return <ArrayGroup key={key} value={child} depth={depth + 1} label={humanizeKey(key)} />;
          }
          if (child !== null && typeof child === 'object') {
            return <CollapsibleNode key={key} label={humanizeKey(key)} value={child} depth={depth + 1} />;
          }
          return (
            <FieldRow
              key={key}
              label={humanizeKey(key)}
              value={formatPrimitive(child)}
              depth={depth + 1}
              muted={child == null}
            />
          );
        })}
        {internalEntries.length > 0 ? <InternalsSection entries={internalEntries} depth={depth} /> : null}
      </div>
    );
  }

  return <FieldRow label={label ?? 'Value'} value={formatPrimitive(value)} depth={depth} muted={value == null} />;
}

function ArrayGroup({
  value,
  depth,
  label,
}: {
  value: unknown[];
  depth: number;
  label?: string;
}): JSX.Element {
  const [allOpen, setAllOpen] = useState(true);
  const [bulkVersion, setBulkVersion] = useState(0);

  function setAllItems(open: boolean): void {
    setAllOpen(open);
    setBulkVersion((current) => current + 1);
  }

  return (
    <div className={depth > 0 ? 'space-y-2' : 'space-y-3'}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">{label ? <SectionLabel label={label} depth={depth} /> : <div />}</div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-md border border-border/70 bg-background px-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setAllItems(!allOpen)}
          >
            {allOpen ? <ChevronsUp className="h-3.5 w-3.5" /> : <ChevronsDown className="h-3.5 w-3.5" />}
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
          <CopyButton
            label={`Copy ${label ?? 'array'}`}
            value={JSON.stringify(value, null, 2)}
            toastTitle="Copied group"
            toastMessage={label ?? 'Array'}
          />
        </div>
      </div>
      {value.map((item, index) => (
        <CollapsibleNode
          key={`${label ?? 'item'}-${index}`}
          label={`Item ${index + 1}`}
          value={item}
          depth={depth + 1}
          bulkOpen={allOpen}
          bulkVersion={bulkVersion}
        />
        ))}
      </div>
  );
}

function CollapsibleNode({
  label,
  value,
  depth,
  bulkOpen,
  bulkVersion,
}: {
  label: string;
  value: unknown;
  depth: number;
  bulkOpen?: boolean;
  bulkVersion?: number;
}): JSX.Element {
  const [open, setOpen] = useState(true);
  const preview = useMemo(() => buildNodePreview(value), [value]);

  useEffect(() => {
    if (bulkVersion !== undefined && bulkOpen !== undefined) {
      setOpen(bulkOpen);
    }
  }, [bulkOpen, bulkVersion]);

  return (
    <div className="rounded-md border border-border/70 bg-muted/20 p-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="flex min-w-0 items-center gap-2">
            <SectionLabel label={label} depth={depth} />
            {!open && preview ? (
              <div className="min-w-0 truncate text-xs text-muted-foreground">
                {preview}
              </div>
            ) : null}
          </div>
        </button>
      </div>
      {open ? (
        <div className="mt-2">
          <StructuredNode value={value} depth={depth} />
        </div>
      ) : null}
    </div>
  );
}

function SectionLabel({ label, depth }: { label: string; depth: number }): JSX.Element {
  return (
    <div className={depth === 0 ? 'text-sm font-semibold text-foreground' : 'text-xs font-semibold uppercase tracking-wide text-muted-foreground'}>
      {label}
    </div>
  );
}

function FieldRow({
  label,
  value,
  depth,
  muted,
}: {
  label: string;
  value: string;
  depth: number;
  muted?: boolean;
}): JSX.Element {
  return (
    <div
      className={`flex flex-wrap items-start gap-x-2 gap-y-1 ${depth > 1 ? 'pl-2' : ''}`}
    >
      <div className="text-xs font-medium text-muted-foreground">{label}:</div>
      <button
        type="button"
        className={cn(
          'rounded-sm text-left text-sm transition-colors hover:underline focus:outline-none focus:ring-1 focus:ring-ring',
          muted ? 'text-muted-foreground' : 'text-foreground',
        )}
        onClick={() => void copyText(value, { title: 'Copied value', message: label })}
      >
        {value}
      </button>
    </div>
  );
}

function CopyButton({
  label,
  value,
  toastTitle,
  toastMessage,
}: {
  label: string;
  value: string;
  toastTitle: string;
  toastMessage: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      aria-label={label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-background text-muted-foreground transition-colors hover:text-foreground"
      onClick={(event) => {
        event.stopPropagation();
        void copyText(value, { title: toastTitle, message: toastMessage });
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function humanizeKey(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'None';
  if (typeof value === 'string') return humanizeValue(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function humanizeValue(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return 'None';
  if (!looksLikeEnumValue(trimmed)) return value;
  return trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function looksLikeEnumValue(value: string): boolean {
  if (!/[_-]/.test(value)) return false;
  if (/[A-Z]/.test(value)) return false;
  if (/\s/.test(value)) return false;
  if (/@|\/|\\|\./.test(value)) return false;
  if (/[0-9]{3,}/.test(value)) return false;
  return /^[a-z0-9_-]+$/.test(value);
}

function partitionObjectEntries(
  entries: Array<[string, unknown]>,
): {
  userFacingEntries: Array<[string, unknown]>;
  internalEntries: Array<[string, unknown]>;
} {
  const userFacingEntries: Array<[string, unknown]> = [];
  const internalEntries: Array<[string, unknown]> = [];

  for (const entry of entries) {
    if (isInternalField(entry[0])) {
      internalEntries.push(entry);
    } else {
      userFacingEntries.push(entry);
    }
  }

  return { userFacingEntries, internalEntries };
}

function isInternalField(key: string): boolean {
  const normalized = key.trim().toLowerCase();
  return (
    normalized === 'id' ||
    normalized.includes('score') ||
    normalized.startsWith('is') ||
    normalized.includes('internal') ||
    normalized.includes('debug') ||
    normalized.includes('trace') ||
    normalized.includes('latency') ||
    normalized.includes('duration') ||
    normalized.includes('confidence')
  );
}

function buildNodePreview(value: unknown): string | null {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    return `${value.length} items`;
  }

  if (value && typeof value === 'object') {
    const { userFacingEntries } = partitionObjectEntries(Object.entries(value as Record<string, unknown>));
    const previewParts = userFacingEntries
      .filter(([, child]) => child !== null && child !== undefined && child !== '')
      .filter(([, child]) => typeof child !== 'object')
      .slice(0, 3)
      .map(([key, child]) => `${humanizeKey(key)}: ${formatPreviewValue(child)}`);

    if (previewParts.length > 0) {
      return previewParts.join(' · ');
    }

    const objectKeys = userFacingEntries
      .filter(([, child]) => child && typeof child === 'object')
      .slice(0, 2)
      .map(([key]) => humanizeKey(key));

    return objectKeys.length > 0 ? objectKeys.join(' · ') : null;
  }

  const primitive = formatPrimitive(value);
  return primitive === 'None' ? null : primitive;
}

function formatPreviewValue(value: unknown): string {
  const formatted = formatPrimitive(value);
  if (formatted.length <= 48) return formatted;
  return `${formatted.slice(0, 45)}...`;
}

async function copyText(
  value: string,
  toast: {
    title: string;
    message: string;
  },
): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    showAppToast({ title: toast.title, message: toast.message, tone: 'success' });
  } catch {
    showAppToast({ title: 'Copy failed', message: toast.message, tone: 'error' });
  }
}

function InternalsSection({
  entries,
  depth,
}: {
  entries: Array<[string, unknown]>;
  depth: number;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-md border border-dashed border-border/70 bg-muted/10 p-3">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        onClick={() => setOpen((current) => !current)}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Internals</div>
      </button>
      {open ? (
        <div className="mt-3 space-y-2 border-t border-border/70 pt-3">
          {entries.map(([key, child]) => {
            if (Array.isArray(child)) {
              if (child.length === 0) {
                return (
                  <FieldRow
                    key={key}
                    label={humanizeKey(key)}
                    value="None"
                    depth={depth + 1}
                    muted
                  />
                );
              }
              return <ArrayGroup key={key} value={child} depth={depth + 1} label={humanizeKey(key)} />;
            }
            if (child !== null && typeof child === 'object') {
              return <CollapsibleNode key={key} label={humanizeKey(key)} value={child} depth={depth + 1} />;
            }
            return (
              <FieldRow
                key={key}
                label={humanizeKey(key)}
                value={formatPrimitive(child)}
                depth={depth + 1}
                muted={child == null}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
